import React, { useCallback, useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import {
  ATMButton,
  ATMField,
  ATMGrid,
  ATMHeader,
  ATMSegment,
  ATMTable,
  ATMInput,
  ATMSelect,
  ATMConfirm,
} from 'shared-it-appmod-ui';
import { actionTypes } from 'src/ducks/request.duck';
import { SettingKey } from 'src/constants';
import { useRequestContext } from 'src/contexts/request.context';
import { useSetting } from 'src/hooks/setting.hook';
import Lang from 'src/libraries/language';
import { getRequestStatus } from 'src/selectors/request.selector';
import { IRequestFormMethods } from 'src/models/request.model';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';

export type IProps = IRequestFormMethods;

const FlightRequestAccountingForm: React.FC<IProps> = ({
  control,
  errors,
  setError,
  clearErrors,
  formState,
}) => {
  const list = useSetting(SettingKey.PlanCategoryList).split(',');
  const { state: requestState, actions: requestActions } = useRequestContext();
  const [confirm, setConfirm] = useState<number>();
  const [isValidCC, setIsValidCC] = useState<boolean>();
  // const [isValidWO, setIsValidWO] = useState<Record<string, boolean>>({});
  const [isValidIO, setIsValidIO] = useState<Record<string, boolean>>({});
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'request.accounting',
  });

  const handleAdd = useCallback(() => {
    append({ workOrder: '', ioNo: '', planCategory: '' });
  }, [append]);

  useEffect(() => {
    if (!fields.length) {
      handleAdd();
    }
  }, [fields, handleAdd]);

  const costFormat = useCallback((value, target) => {
    const text = value.replace('-', '').replace(/(.{4})(?=.)/g, '$1-');

    const position: number = target.selectionStart;

    setTimeout(() => {
      let skip = 0;

      if (text.charAt(position) === '-' || text.charAt(position - 1) === '-') {
        skip = text.length !== value.length ? 2 : 1;
      }

      // eslint-disable-next-line no-param-reassign
      target.selectionEnd = position + skip;
    });

    return text;
  }, []);

  useEffect(() => {
    if (isValidCC !== undefined) {
      if (!isValidCC && setError) {
        setError('request.costCenter', {
          type: 'costCenterCheck',
          message: Lang.MSG_ERROR_COST_CENTER_NOT_VALID,
        });
      } else if (clearErrors) {
        clearErrors(['request.costCenter']);
      }
    }
  }, [formState?.isSubmitting, fields, isValidCC, clearErrors, setError]);

  const verifyCostCenter = useCallback(
    async (value) => {
      if (value) {
        const response = await requestActions.verifyCostCenterGET(value);
        if (response.payload?.Cost_Center.cost_center) {
          setIsValidCC(true);
        } else {
          setIsValidCC(false);
        }
      }
    },
    [requestActions, setIsValidCC]
  );

  // useEffect(() => {
  //   Object.entries(isValidWO).map(([id, value]) => {
  //     const index = fields.findIndex((item) => item.id === id);

  //     if (!value && setError) {
  //       setError(`request.accounting[${index}].workOrder`, {
  //         type: 'workOrderCheck',
  //         message: Lang.MSG_ERROR_WORK_ORDER_NOT_VALID,
  //       });
  //     } else if (clearErrors) {
  //       clearErrors([
  //         `request.accounting[${index}].ioNo`,
  //         `request.accounting[${index}].workOrder`,
  //       ]);
  //     }
  //   });
  // }, [formState?.isSubmitting, fields, isValidWO, clearErrors, setError]);

  // const verifyWorkOrder = (index, value) => {
  //     if (value) {
  //       console.log(typeof value);
  //       setIsValidWO((values) => ({
  //           ...values,
  //           [index]: !Number.isNaN(value),
  //         }));
  //     } else {
  //       setIsValidWO((values) =>
  //         Object.keys(values).reduce((items, key) => {
  //           const itemList = {
  //             ...items,
  //           };

  //           if (key !== index) {
  //             itemList[key] = values[key];
  //           }

  //           return itemList;
  //         }, {})
  //       );
  //     }
  // };

  useEffect(() => {
    Object.entries(isValidIO).map(([id, value]) => {
      const index = fields.findIndex((item) => item.id === id);

      if (value && clearErrors) {
        clearErrors([
          `request.accounting[${index}].ioNo`,
          `request.accounting[${index}].workOrder`,
        ]);
      } else if (!value && setError) {
        setError(`request.accounting[${index}].ioNo`, {
          type: 'ioNoCheck',
          message: Lang.MSG_ERROR_IO_NUMBER_NOT_VALID,
        });
      }
    });
  }, [formState?.isSubmitting, fields, isValidIO, clearErrors, setError]);

  const verifyIONumber = useCallback(
    async (index, value) => {
      if (value) {
        const response = await requestActions.verifyIONumberGET(value);

        if (response.payload) {
          setIsValidIO((values) => ({
            ...values,
            [index]: response.payload?.valid ?? false,
          }));
        }
      } else {
        setIsValidIO((values) =>
          Object.keys(values).reduce((items, key) => {
            const itemList = {
              ...items,
            };

            if (key !== index) {
              itemList[key] = values[key];
            }

            return itemList;
          }, {})
        );
      }
    },
    [requestActions, setIsValidIO]
  );

  return (
    <ATMSegment>
      <ATMHeader>{Lang.TTL_REQUEST_ACCOUNTING}</ATMHeader>
      <ATMGrid>
        <ATMGrid.Column>
          <ATMField
            autoComplete="off"
            name="request.costCenter"
            width={3}
            maxLength={9}
            label={Lang.LBL_REQUEST_COST_CENTER}
            placeholder={Lang.LBL_PLACEHOLDER_COST_CENTER}
            as={ATMInput}
            onChange={([{ target }, { value }]) => costFormat(value, target)}
            onBlur={(_, value) => {
              verifyCostCenter(value);
            }}
            icon={isValidCC && 'check green'}
            loading={
              getRequestStatus(
                requestState,
                actionTypes.REQUEST_VERIFY_COST_CENTER
              ).fetching
            }
            control={control}
            error={errors.request?.costCenter}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <ATMTable>
        <ATMTable.Header>
          <ATMTable.Row>
            <ATMTable.HeaderCell width={5}>
              {Lang.LBL_REQUEST_WORK_ORDER_NUMBER}
            </ATMTable.HeaderCell>
            <ATMTable.HeaderCell width={5}>
              <LabelRequired>{Lang.LBL_REQUEST_IO_NUMBER}</LabelRequired>
            </ATMTable.HeaderCell>
            <ATMTable.HeaderCell width={5}>
              {Lang.LBL_REQUEST_PLAN_CATEGORY}
            </ATMTable.HeaderCell>
            <ATMTable.HeaderCell width={1} />
          </ATMTable.Row>
        </ATMTable.Header>

        <ATMTable.Body>
          {fields.map((item, i) => (
            <ATMTable.Row key={item.id}>
              <ATMTable.Cell>
                <ATMField
                  autoComplete="off"
                  as={ATMInput}
                  name={`request.accounting[${i}].workOrder`}
                  maxLength={7}
                  type="number"
                  pattern="\d*"
                  control={control}
                  // icon={isValidWO[item.id ?? ''] && 'check green'}
                  placeholder={Lang.LBL_ENTER}
                  error={
                    Array.isArray(errors.request?.accounting)
                      ? errors.request?.accounting[i]?.workOrder
                      : false
                  }
                  // onBlur={(_, value) => {
                  //   verifyWorkOrder(item.id, value);
                  // }}
                  loading={
                    getRequestStatus(
                      requestState,
                      actionTypes.REQUEST_VERIFY_WORK_ORDER
                    ).fetching
                  }
                />
              </ATMTable.Cell>
              <ATMTable.Cell>
                <ATMField
                  autoComplete="off"
                  as={ATMInput}
                  name={`request.accounting[${i}].ioNo`}
                  control={control}
                  icon={isValidIO[item.id ?? ''] && 'check green'}
                  maxLength={12}
                  placeholder={Lang.LBL_ENTER}
                  error={
                    Array.isArray(errors.request?.accounting)
                      ? errors.request?.accounting[i]?.ioNo
                      : false
                  }
                  onBlur={(_, value) => {
                    verifyIONumber(item.id, value);
                  }}
                  loading={
                    getRequestStatus(
                      requestState,
                      actionTypes.REQUEST_VERIFY_IO_NUMBER
                    ).fetching
                  }
                />
              </ATMTable.Cell>
              <ATMTable.Cell>
                <ATMField
                  as={ATMSelect}
                  name={`request.accounting[${i}].planCategory`}
                  placeholder={Lang.LBL_SELECT}
                  control={control}
                  options={list.map((value, key) => ({
                    key,
                    value,
                    text: value,
                  }))}
                  selectOnBlur={false}
                  onChange={([_, { value }]) => value}
                  clearable
                  error={
                    Array.isArray(errors.request?.accounting)
                      ? errors.request?.accounting[i]?.planCategory
                      : false
                  }
                />
              </ATMTable.Cell>
              <ATMTable.Cell textAlign="center">
                {fields.length > 1 && (
                  <ATMButton
                    icon="trash alternate outline"
                    type="button"
                    onClick={() => setConfirm(i)}
                  />
                )}
              </ATMTable.Cell>
            </ATMTable.Row>
          ))}
        </ATMTable.Body>
        <ATMTable.Footer>
          <ATMTable.Row>
            <ATMTable.HeaderCell colSpan={4}>
              <ATMButton
                secondary
                icon="plus"
                type="button"
                onClick={handleAdd}
                disabled={fields.length >= 4}
                content={Lang.LBL_ADD}
              />
            </ATMTable.HeaderCell>
          </ATMTable.Row>
        </ATMTable.Footer>
      </ATMTable>
      <ATMConfirm
        size="tiny"
        header={Lang.TTL_CONFIRM_DELETE}
        open={confirm !== undefined}
        content={Lang.MSG_CONFIRM_DELETE_ACCOUNTING_INFO}
        onConfirm={() => {
          remove(confirm);
          setConfirm(undefined);
        }}
        onCancel={() => setConfirm(undefined)}
      />
    </ATMSegment>
  );
};

export default FlightRequestAccountingForm;
